import request from '../../library/request';
import store from '../../store';
import setGlobalState from '../setGlobalState';
import {uploadFile} from '../user/userActions';
import {wrapXHR} from '../../functions/wrapXHR';
import getColor from '../../functions/getColor';

const getEid = () => store.getState().event.eid;

// Some tickets (e.g. cash tickets) arrive without a color. Assign a fallback
// once, here at the store boundary, so every consumer can rely on ticket.color.
const ensureColors = (newTickets = [], existingTickets = []) => {
    const accumulated = [...existingTickets];
    return newTickets.map(ticket => {
        const normalized = ticket.color ? ticket : {...ticket, color: getColor(null, accumulated)};
        accumulated.push(normalized);
        return normalized;
    });
};

const updateCouponTicketClubs = (tid, committees) => {
    const currentSettings = store.getState().event?.settings || {};
    if(!currentSettings.clubs) {
        setGlobalState('event.settings.clubs', {});
    }
    
    const currentCouponTicketClubs = currentSettings.clubs?.couponTicketClubs || [];
    
    if(!committees || committees.length === 0) {
        const updatedCouponTicketClubs = currentCouponTicketClubs.filter(item => item.tid !== tid);
        setGlobalState('event.settings.clubs.couponTicketClubs', updatedCouponTicketClubs);
    } else {
        let updatedCouponTicketClubs = currentCouponTicketClubs.filter(item => item.tid !== tid);
        committees.forEach(committee => {
            updatedCouponTicketClubs.push({
                ...committee,
                cid: committee.cid || null
            });
        });
        
        setGlobalState('event.settings.clubs.couponTicketClubs', updatedCouponTicketClubs);
    }
};

export const getSearchTickets = async (search) => {
    const res = await request('tickets.getSearchTickets', 'get', `/event/${getEid()}/tickets`, {search});
    setGlobalState('tickets.searchTickets', ensureColors(res?.ticketsInfo, store.getState().tickets.tickets));
};

export const getEventTickets = async () => {
    const limit = 20;
    const storeTickets=store.getState().tickets.tickets;
    const res = await request('tickets.getEventTickets', 'get', `/event/${getEid()}/tickets`,
        {limit, offset:storeTickets?.length});
    let tickets = storeTickets || [];
    if(!storeTickets.length){
        setGlobalState('tickets.ticketsIcon', res?.ticketsIcon);
        if(!store.getState().event.settings?.report)
            setGlobalState('event.settings.report',{attendanceReport:res?.attendanceReport});
    }
    if(res?.ticketsInfo){
        tickets=[...storeTickets, ...ensureColors(res.ticketsInfo, storeTickets)];
        setGlobalState('tickets.tickets', tickets);
        if(res.ticketsInfo.length < limit) {
            setGlobalState('tickets.isAcceptedAll', true);
        } else {
            getEventTickets();
        }
    }
    return tickets;
};

export const checkEventLocation =  (locationMap,location) => {
    const event = store.getState().event;
    if(!event.location && !event.locationMap && locationMap){
        setGlobalState('event', {...event, location:location,locationMap:locationMap});
    }
};

export const updateTicket = (tid, fields) =>
    wrapXHR('tickets.updateTicketFullXHR', async () => {
        if(fields.imgData?.image) { fields.imgData.image = await uploadFile(fields.imgData.image, '', 'ticketImage', getEid()); }
        const index = store.getState().tickets.tickets.findIndex(ticket => ticket.tid === tid);
        setGlobalState('tickets.currentTicket', store.getState().tickets.tickets[index]);
        let res = await request('tickets.updateTicket', 'put', `/event/${getEid()}/ticket/${tid}`, fields);
        checkEventLocation(fields?.location?.locationMap, fields?.location?.location);
        const value = store.getState().tickets.tickets[index];
        setGlobalState(`tickets.tickets[${index}]`, {...value, ...res.ticketUpdated});
        setGlobalState('tickets.currentTicket', res.ticketUpdated);
        if(fields.committees !== undefined) {
            updateCouponTicketClubs(tid, fields.committees);
        }
        return res.errors;
    });

export const updateTicketWeight = async (tickets = []) => {
    const tidWeights = tickets.map((i, index) => ({tid: i.tid, weight: index}));
    setGlobalState('tickets.tickets', tickets);
    await request('tickets.updateTicketWeight', 'put', `/event/${getEid()}/tickets`, {tidWeights});
};
export const updateEventsWeight = async (clusterEid, fields, updateWeight, cgid) => {
    let groupIndex = store.getState().cluster.groups.findIndex(item => item.cgid === cgid);
    setGlobalState(`cluster.groups[${groupIndex}].events`, updateWeight);
    await request('cluster.updateEventsWeight', 'put', `/cluster/${clusterEid}/events`, fields);

};
export const deleteTicket = async (tid) => {
    const res = await request('tickets.deleteTicket', 'delete', `/event/${getEid()}/ticket/${tid}`);
    if(res) {
        const index = store.getState().tickets.tickets.findIndex(ticket => ticket.tid === tid);
        const tickets = [...store.getState().tickets.tickets];
        tickets.splice(index, 1);
        setGlobalState('tickets.tickets', tickets);
    }
    
};

export const duplicateTicket = async (tid, fields) => {
    const res = await request('tickets.duplicateTicket', 'post', `/event/${getEid()}/ticket/${tid}/duplicate`, fields);
    const [newTicket] = ensureColors([res.newTicket], store.getState().tickets.tickets);
    setGlobalState('tickets.tickets', [...store.getState().tickets.tickets, newTicket]);
    setGlobalState('tickets.currentTicket', newTicket);
    const ticketGroups = store.getState().tickets.groups?.filter(g => g.tickets?.some(t => t.tid === tid));
    ticketGroups.forEach(g => {
        const index = store.getState().tickets.groups.findIndex(group => group.gid === g.gid);
        setGlobalState(`tickets.groups[${index}].tickets`, [...store.getState().tickets.groups[index].tickets, newTicket]);
    });
    return res;
};

export const getEventGroups = async () => {
    const res = await request('tickets.getEventGroups', 'get', `/event/${getEid()}/groups`);
    setGlobalState('tickets.groups', res.groupsInfo);
};

export const updateGroup = (gid, fields) =>
    wrapXHR('tickets.updateGroupFullXHR', async () => {
        if(fields.groupImage) { fields.groupImage = await uploadFile(fields.groupImage, '', 'groupImage', getEid()); }
        const res = await request('tickets.updateGroup', 'put', `/event/${getEid()}/group/${gid}`, fields);
        const index = store.getState().tickets.groups.findIndex(g => g.gid === gid);
        const value = store.getState().tickets.groups[index];
        setGlobalState(`tickets.groups[${index}]`, {...value, ...res.updateFields});
    });

export const addGroup = (fields) =>
    wrapXHR('tickets.addGroupFullXHR', async () => {
        if(fields.groupImage) { fields.groupImage = await uploadFile(fields.groupImage, '', 'groupImage', getEid()); }
        const res = await request('tickets.addGroup', 'post', `/event/${getEid()}/group`, fields);
        setGlobalState('tickets.groups', [...store.getState().tickets.groups, res.group]);
    });

export const deleteGroup = async (gid) => {
    await request('tickets.deleteGroup', 'delete', `/event/${getEid()}/group/${gid}`);
    const index = store.getState().tickets.groups.findIndex(g => g.gid === gid);
    const groups = [...store.getState().tickets.groups];
    groups.splice(index, 1);
    setGlobalState('tickets.groups', groups);
};

export const getTicket = async (tid) => {
    const res = await request('tickets.getTicket', 'get', `/event/${getEid()}/ticket/${tid}`);
    setGlobalState('tickets.currentTicket', res.ticketInfo);
};

// Adds a ticket created as a side-effect elsewhere (e.g. the casual ticket
// created when saving a lecture) into the list, unique by tid, so it shows
// without a full page refresh. No-op if it's already there.
export const addTicketToList = (ticket) => {
    if(!ticket?.tid) return;
    const existing = store.getState().tickets.tickets;
    if(existing.some(t => t.tid === ticket.tid)) return;
    const [withColor] = ensureColors([ticket], existing);
    setGlobalState('tickets.tickets', [...existing, withColor]);
};

export const addTicket = (fields) =>
    wrapXHR('tickets.addTicketFullXHR', async () => {
        if(fields.imgData?.image) { fields.imgData.image = await uploadFile(fields.imgData.image, '', 'ticketImage', getEid()); }
        const res = await request('tickets.addTicket', 'post', `/event/${getEid()}/ticket`, fields);
        const [ticket] = ensureColors([res.ticket], store.getState().tickets.tickets);
        setGlobalState('tickets.tickets', [...store.getState().tickets.tickets, ticket]);
        setGlobalState('tickets.currentTicket', ticket);
        checkEventLocation(fields?.location?.locationMap, fields?.location?.location);
        if(res?.ready)
            setGlobalState('event.ready', res.ready);    
        // Handle committees update if provided
        if(fields.committees !== undefined && res.ticket?.tid) {
            updateCouponTicketClubs(res.ticket.tid, fields.committees);
        }
    });

export const updateTicketsIcon = async (icon) => {
    await request('tickets.updateTicketsIcon', 'put', `/event/${getEid()}/ticketsIcon`, {icon});
    setGlobalState('tickets.ticketsIcon',icon);
};

export const updateGuideReport = async (tid,uid,status) => {
    await request('tickets.updateGuideReport', 'put', `/event/${getEid()}/ticket/${tid}/guide/${uid}/report`, {status});
    const tickets= store.getState().tickets.tickets;
    const index=tickets.findIndex(ticket=>ticket.tid===tid);
    const userIndex=tickets[index].users.findIndex(user=>user.uid===uid);
    setGlobalState(`tickets.tickets[${index}].users[${userIndex}].attendanceReport`,status);
};
export const sendTicket = async (mailToSend, subject, emailContent, eid, lang) => {
    await request('tickets.sendTicket', 'post', `/event/${eid}/mail`,{mailToSend,subject,emailContent,lang});
};

